import React from "react";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Link } from "react-router";
import blogData from "@/data/blog-posts.json";

// Get first 3 posts from generated data for the landing page
const BLOG_POSTS = blogData.fr.slice(0, 3).map((post, index) => ({
  id: String(index + 1).padStart(3, '0'),
  slug: post.slug,
  title: post.title,
  description: post.description,
  category: post.category,
  date: post.date,
  readTime: post.readTime,
  image: post.coverImage.startsWith('/')
    ? `https://images.pexels.com/photos/5642426/pexels-photo-5642426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600`
    : post.coverImage,
}));

const BlogCard = ({ post, featured = false }) => {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group block ${featured ? "lg:col-span-2 lg:row-span-2" : ""}`}
    >
      <article className="h-full flex flex-col bg-white border border-[#0C2340]/8 overflow-hidden hover:border-[#E8611A]/50 transition-colors duration-500">
        <div className={`relative overflow-hidden ${featured ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
          <img
            src={post.image}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E8611A] text-white font-body text-[0.65rem] uppercase tracking-[0.2em]">
              {post.category}
            </span>
          </div>
        </div>
        <div className="flex-1 flex flex-col p-6">
          <div className="flex items-center gap-4 mb-4 text-[#636E72]">
            <span className="flex items-center gap-1.5 font-body text-[0.65rem] uppercase tracking-[0.2em]">
              <Calendar size={12} />
              {post.date}
            </span>
            <span className="flex items-center gap-1.5 font-body text-[0.65rem] uppercase tracking-[0.2em]">
              <Clock size={12} />
              {post.readTime}
            </span>
          </div>
          <h3 className={`font-heading font-bold text-[#0C2340] leading-tight mb-3 group-hover:text-[#E8611A] transition-colors ${featured ? "text-2xl md:text-3xl" : "text-lg md:text-xl"}`}>
            {post.title}
          </h3>
          <p className="font-body text-[#2D3436] text-sm leading-relaxed mb-4 flex-1">
            {post.description}
          </p>
          <div className="flex items-center gap-2 font-body text-[0.7rem] uppercase tracking-[0.2em] font-semibold text-[#E8611A] group-hover:gap-3 transition-all">
            Lire l'article
            <ArrowRight size={14} />
          </div>
        </div>
      </article>
    </Link>
  );
};

const Blog = () => {
  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 border-t border-[#0C2340]/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="mikassa-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            Blog & Ressources
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <h2
            data-testid="blog-title"
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Conseils & actualités<br />
            <span className="text-[#E8611A]">pour les pros de l'immobilier.</span>
          </h2>
          <div className="lg:col-span-4 flex justify-start lg:justify-end">
            <Link
              to="/blog"
              className="mikassa-btn-outline inline-flex items-center gap-2"
            >
              Voir tous les articles
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {BLOG_POSTS.map((post, idx) => (
            <BlogCard key={post.id} post={post} featured={idx === 0} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
